/**
 * Performance **Manage** — review cycles + goals for an employee, `/hr/performance`,
 * WP-W8. `web/shapes/hr-performance.md` §6 is the agreed shape.
 *
 * Rendered only for a session holding `hr.manage_review_cycles` (`PerformanceScreen`
 * decides that, the same client-side honesty gate `LeaveScreen`'s approvals section
 * uses — never the authorisation, which the server still runs on every call).
 *
 * Two surfaces, and they authorise differently, which is why they are two
 * subsections:
 *
 *  - **Review cycles** are company configuration (`hr.view_reviews` to list,
 *    `hr.manage_review_cycles` to create) — like `OnboardingTemplatesPanel`'s templates.
 *    Listing is gated on a **different** code from creating, so an admin who holds only
 *    `hr.manage_review_cycles` still creates a cycle even while this list renders its
 *    own `403` in place (§3 of the shape).
 *  - **Goals for an employee** are about one employee, picked through
 *    `EmployeePicker` (Task T2-B) rather than a typed id — this screen is that
 *    component's one proof-of-adoption site. Selecting a row loads their goals
 *    immediately; there is no separate "load" step any more.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type {
  Goal,
  GoalCreateRequest,
  GoalStatus,
  ReviewCycle,
  ReviewCycleCreateRequest,
  ReviewCycleStatus,
} from '../../api/hr';
import { GOAL_STATUSES, REVIEW_CYCLE_STATUSES } from '../../api/hr';
import {
  GOAL_STATUS_BADGE,
  GOAL_STATUS_LABEL,
  REVIEW_CYCLE_STATUS_BADGE,
  REVIEW_CYCLE_STATUS_LABEL,
} from '../../hr/performance';
import type { QueryStatus } from '../../api/useApiQuery';
import type { WorkforceApiError } from '../../api/errors';
import { DateField } from '../../components/DateField';
import { EmployeePicker, type EmployeePickerValue } from '../../components/EmployeePicker';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { Badge } from '../../states/Badge';
import { EmptyState } from '../../states/EmptyState';
import { SEVERITY_ICON } from '../../states/icons';
import { Skeleton } from '../../states/Skeleton';
import { StatusNote } from '../../states/StatusNote';

const NAME_MAX = 300;
const TITLE_MAX = 500;

export interface PerformanceManagePanelProps {
  readonly cyclesStatus: QueryStatus;
  readonly cycles: readonly ReviewCycle[] | null;
  readonly cyclesError: WorkforceApiError | null;
  readonly onReloadCycles: () => void;
  readonly cycleCreatePending: boolean;
  readonly cycleCreateError: WorkforceApiError | null;
  readonly cycleCreated: ReviewCycle | null;
  readonly onCreateCycle: (body: ReviewCycleCreateRequest) => void;

  readonly employeeId: string | null;
  readonly onLoadEmployee: (id: string) => void;
  readonly goalsStatus: QueryStatus;
  readonly goals: readonly Goal[] | null;
  readonly goalsError: WorkforceApiError | null;
  readonly onReloadGoals: () => void;
  readonly goalCreatePending: boolean;
  readonly goalCreateError: WorkforceApiError | null;
  readonly goalCreated: Goal | null;
  readonly onCreateGoal: (body: GoalCreateRequest) => void;
}

export function PerformanceManagePanel(props: PerformanceManagePanelProps): ReactNode {
  const { t } = useI18n();
  const [name, setName] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [status, setStatus] = useState<'' | ReviewCycleStatus>('');

  const [pickerValue, setPickerValue] = useState<EmployeePickerValue | null>(null);
  const [goalTitle, setGoalTitle] = useState('');
  const [goalDescription, setGoalDescription] = useState('');
  const [goalMeasure, setGoalMeasure] = useState('');
  const [goalCycleId, setGoalCycleId] = useState('');
  const [goalStatus, setGoalStatus] = useState<'' | GoalStatus>('');
  const [goalWeight, setGoalWeight] = useState('');

  const nameReady = name.trim().length > 0;
  const goalReady = goalTitle.trim().length > 0;

  return (
    <section className="wp-section" aria-labelledby="w8-manage-heading" data-testid="w8-manage">
      <h2 className="wp-section__title" id="w8-manage-heading">
        {t('hr.performance.manage.title')}
      </h2>

      {/* ── Review cycles ───────────────────────────────────────────────────── */}
      <div className="wp-section__subsection" data-testid="w8-cycles">
        <h3 className="wp-section__title">{t('hr.performance.cycles.title')}</h3>
        <p className="wp-small wp-muted">{t('hr.performance.cycles.intro')}</p>

        <form
          className="wp-filters"
          onSubmit={(event) => {
            event.preventDefault();
            if (!nameReady || props.cycleCreatePending) return;
            const trimmedStart = periodStart.trim();
            const trimmedEnd = periodEnd.trim();
            props.onCreateCycle({
              name: name.trim(),
              ...(trimmedStart === '' ? {} : { period_start: trimmedStart }),
              ...(trimmedEnd === '' ? {} : { period_end: trimmedEnd }),
              ...(status === '' ? {} : { status }),
            });
          }}
        >
          <div className="wp-filters__group">
            <label className="wp-field__label" htmlFor="w8-cycle-name">
              {t('hr.performance.cycles.create.name')}
            </label>
            <input
              className="wp-field__input"
              id="w8-cycle-name"
              data-testid="w8-cycle-name"
              type="text"
              value={name}
              maxLength={NAME_MAX}
              autoComplete="off"
              onChange={(event) => {
                setName(event.target.value);
              }}
            />
          </div>
          <DateField
            className="wp-filters__group"
            id="w8-cycle-period-start"
            testId="w8-cycle-period-start"
            label={t('hr.performance.cycles.create.periodStart')}
            value={periodStart}
            onChange={setPeriodStart}
          />
          <DateField
            className="wp-filters__group"
            id="w8-cycle-period-end"
            testId="w8-cycle-period-end"
            label={t('hr.performance.cycles.create.periodEnd')}
            value={periodEnd}
            onChange={setPeriodEnd}
          />
          <div className="wp-filters__group">
            <label className="wp-field__label" htmlFor="w8-cycle-status">
              {t('hr.performance.cycles.create.status')}
            </label>
            <select
              className="wp-field__input"
              id="w8-cycle-status"
              data-testid="w8-cycle-status"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value as '' | ReviewCycleStatus);
              }}
            >
              <option value="">{t('hr.performance.cycles.create.statusDefault')}</option>
              {REVIEW_CYCLE_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {t(REVIEW_CYCLE_STATUS_LABEL[value])}
                </option>
              ))}
            </select>
          </div>
          <div className="wp-filters__actions">
            <button
              className="wp-button wp-button--primary"
              type="submit"
              data-testid="w8-cycle-submit"
              disabled={!nameReady || props.cycleCreatePending}
            >
              {props.cycleCreatePending
                ? t('hr.performance.cycles.create.submitting')
                : t('hr.performance.cycles.create.submit')}
            </button>
          </div>
        </form>
        {props.cycleCreateError === null ? null : (
          <ApiErrorNote
            error={props.cycleCreateError}
            subject={t('hr.performance.cycles.subject')}
            testId="w8-cycle-create-error"
          />
        )}
        {props.cycleCreated === null ? null : (
          <StatusNote
            severity="low"
            title={t('hr.performance.cycles.create.ok', { name: props.cycleCreated.name })}
            testId="w8-cycle-create-ok"
          />
        )}

        {props.cyclesStatus === 'loading' ? (
          <Skeleton
            rows={2}
            variant="block"
            label={t('hr.performance.cycles.subject')}
            testId="w8-cycles-skeleton"
          />
        ) : null}
        {props.cyclesStatus === 'error' && props.cyclesError !== null ? (
          <ApiErrorNote
            error={props.cyclesError}
            onRetry={props.onReloadCycles}
            subject={t('hr.performance.cycles.subject')}
            testId="w8-cycles-error"
          />
        ) : null}
        {props.cycles === null ? null : props.cycles.length === 0 ? (
          <EmptyState
            title={t('hr.performance.cycles.empty.title')}
            description={t('hr.performance.cycles.empty.body')}
            testId="w8-cycles-empty"
          />
        ) : (
          <ul className="wp-rows" data-testid="w8-cycles-list">
            {props.cycles.map((cycle) => (
              <li className="wp-row" key={cycle.id} data-testid={`w8-cycle-${cycle.id}`}>
                <div className="wp-row__main">
                  <p>
                    <Badge
                      severity={REVIEW_CYCLE_STATUS_BADGE[cycle.status]}
                      icon={SEVERITY_ICON[REVIEW_CYCLE_STATUS_BADGE[cycle.status]]}
                      label={t(REVIEW_CYCLE_STATUS_LABEL[cycle.status])}
                      testId={`w8-cycle-status-${cycle.id}`}
                    />{' '}
                    <span>{cycle.name}</span>
                  </p>
                  {cycle.period_start === null && cycle.period_end === null ? null : (
                    <p className="wp-small wp-muted">
                      {t('hr.performance.cycles.row.period', {
                        start: cycle.period_start ?? t('hr.performance.cycles.row.noDate'),
                        end: cycle.period_end ?? t('hr.performance.cycles.row.noDate'),
                      })}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ── Goals for an employee ───────────────────────────────────────────── */}
      <div className="wp-section__subsection" data-testid="w8-manage-goals">
        <h3 className="wp-section__title">{t('hr.performance.manageGoals.title')}</h3>
        <p className="wp-small wp-muted">{t('hr.performance.manageGoals.intro')}</p>

        <div className="wp-filters">
          <div className="wp-filters__group">
            <EmployeePicker
              value={pickerValue}
              onChange={(next) => {
                setPickerValue(next);
                // Selecting a row loads it immediately — the picker's own
                // selection IS the "load" action; there is no separate submit
                // step left to gate on a trimmed, typed id any more.
                if (next !== null) props.onLoadEmployee(next.id);
              }}
              label={t('hr.performance.manageGoals.employeeLabel')}
              testId="w8-manage-goals-employee"
            />
          </div>
        </div>

        {props.employeeId === null ? (
          <EmptyState
            title={t('hr.performance.manageGoals.none.title')}
            description={t('hr.performance.manageGoals.none.body')}
            testId="w8-manage-goals-none"
          />
        ) : (
          <>
            <form
              className="wp-filters"
              onSubmit={(event) => {
                event.preventDefault();
                if (props.employeeId === null || !goalReady || props.goalCreatePending) return;
                const trimmedDescription = goalDescription.trim();
                const trimmedMeasure = goalMeasure.trim();
                const trimmedCycle = goalCycleId.trim();
                const trimmedWeight = goalWeight.trim();
                props.onCreateGoal({
                  employee_id: props.employeeId,
                  title: goalTitle.trim(),
                  ...(trimmedCycle === '' ? {} : { cycle_id: trimmedCycle }),
                  ...(trimmedDescription === '' ? {} : { description: trimmedDescription }),
                  ...(trimmedMeasure === '' ? {} : { measure: trimmedMeasure }),
                  ...(goalStatus === '' ? {} : { status: goalStatus }),
                  ...(trimmedWeight === '' ? {} : { weight: Number(trimmedWeight) }),
                });
              }}
            >
              <div className="wp-filters__group">
                <label className="wp-field__label" htmlFor="w8-manage-goal-title">
                  {t('hr.performance.manageGoals.create.goalTitle')}
                </label>
                <input
                  className="wp-field__input"
                  id="w8-manage-goal-title"
                  data-testid="w8-manage-goal-title"
                  type="text"
                  value={goalTitle}
                  maxLength={TITLE_MAX}
                  autoComplete="off"
                  onChange={(event) => {
                    setGoalTitle(event.target.value);
                  }}
                />
              </div>
              <div className="wp-filters__group">
                <label className="wp-field__label" htmlFor="w8-manage-goal-description">
                  {t('hr.performance.manageGoals.create.description')}
                </label>
                <textarea
                  className="wp-field__input"
                  id="w8-manage-goal-description"
                  data-testid="w8-manage-goal-description"
                  maxLength={4000}
                  value={goalDescription}
                  onChange={(event) => {
                    setGoalDescription(event.target.value);
                  }}
                />
              </div>
              <div className="wp-filters__group">
                <label className="wp-field__label" htmlFor="w8-manage-goal-measure">
                  {t('hr.performance.manageGoals.create.measure')}
                </label>
                <input
                  className="wp-field__input"
                  id="w8-manage-goal-measure"
                  data-testid="w8-manage-goal-measure"
                  type="text"
                  maxLength={500}
                  value={goalMeasure}
                  autoComplete="off"
                  onChange={(event) => {
                    setGoalMeasure(event.target.value);
                  }}
                />
              </div>
              <div className="wp-filters__group">
                <label className="wp-field__label" htmlFor="w8-manage-goal-cycle">
                  {t('hr.performance.cycleIdLabel')}
                </label>
                <input
                  className="wp-field__input"
                  id="w8-manage-goal-cycle"
                  data-testid="w8-manage-goal-cycle"
                  type="text"
                  inputMode="numeric"
                  value={goalCycleId}
                  autoComplete="off"
                  onChange={(event) => {
                    setGoalCycleId(event.target.value);
                  }}
                />
              </div>
              <div className="wp-filters__group">
                <label className="wp-field__label" htmlFor="w8-manage-goal-status">
                  {t('hr.performance.manageGoals.create.status')}
                </label>
                <select
                  className="wp-field__input"
                  id="w8-manage-goal-status"
                  data-testid="w8-manage-goal-status"
                  value={goalStatus}
                  onChange={(event) => {
                    setGoalStatus(event.target.value as '' | GoalStatus);
                  }}
                >
                  <option value="">{t('hr.performance.manageGoals.create.statusDefault')}</option>
                  {GOAL_STATUSES.map((value) => (
                    <option key={value} value={value}>
                      {t(GOAL_STATUS_LABEL[value])}
                    </option>
                  ))}
                </select>
              </div>
              <div className="wp-filters__group">
                <label className="wp-field__label" htmlFor="w8-manage-goal-weight">
                  {t('hr.performance.manageGoals.create.weight')}
                </label>
                <input
                  className="wp-field__input"
                  id="w8-manage-goal-weight"
                  data-testid="w8-manage-goal-weight"
                  type="number"
                  min="0"
                  max="100"
                  value={goalWeight}
                  onChange={(event) => {
                    setGoalWeight(event.target.value);
                  }}
                />
              </div>
              <div className="wp-filters__actions">
                <button
                  className="wp-button wp-button--primary"
                  type="submit"
                  data-testid="w8-manage-goal-submit"
                  disabled={!goalReady || props.goalCreatePending}
                >
                  {props.goalCreatePending
                    ? t('hr.performance.manageGoals.create.submitting')
                    : t('hr.performance.manageGoals.create.submit')}
                </button>
              </div>
            </form>
            {props.goalCreateError === null ? null : (
              <ApiErrorNote
                error={props.goalCreateError}
                subject={t('hr.performance.manageGoals.subject')}
                testId="w8-manage-goal-create-error"
              />
            )}
            {props.goalCreated === null ? null : (
              <StatusNote
                severity="low"
                title={t('hr.performance.manageGoals.create.ok', {
                  title: props.goalCreated.title,
                })}
                testId="w8-manage-goal-create-ok"
              />
            )}

            {props.goalsStatus === 'loading' ? (
              <Skeleton
                rows={2}
                variant="block"
                label={t('hr.performance.manageGoals.subject')}
                testId="w8-manage-goals-skeleton"
              />
            ) : null}
            {props.goalsStatus === 'error' && props.goalsError !== null ? (
              <ApiErrorNote
                error={props.goalsError}
                onRetry={props.onReloadGoals}
                subject={t('hr.performance.manageGoals.subject')}
                testId="w8-manage-goals-error"
              />
            ) : null}
            {props.goals === null ? null : props.goals.length === 0 ? (
              <EmptyState
                title={t('hr.performance.manageGoals.empty.title')}
                description={t('hr.performance.manageGoals.empty.body')}
                testId="w8-manage-goals-empty"
              />
            ) : (
              <ul className="wp-rows" data-testid="w8-manage-goals-list">
                {props.goals.map((goal) => (
                  <li className="wp-row" key={goal.id} data-testid={`w8-manage-goal-${goal.id}`}>
                    <div className="wp-row__main">
                      <p>
                        <Badge
                          severity={GOAL_STATUS_BADGE[goal.status]}
                          icon={SEVERITY_ICON[GOAL_STATUS_BADGE[goal.status]]}
                          label={t(GOAL_STATUS_LABEL[goal.status])}
                          testId={`w8-manage-goal-status-${goal.id}`}
                        />{' '}
                        <span>{goal.title}</span>
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </section>
  );
}
